import { NavLink, useParams } from 'react-router'

import { basePath, MODULOS, type Modulo } from '@/app/navigation'
import { useSession } from '@/auth/session'
import { useFlags } from '@/flags/flags'
import { usePermissoes } from '@/permissions/usePermission'
import { acessoDe } from '@/permissions/resolver'
import { clienteEmFoco } from '@/tenant/persistencia'

/**
 * Module sidebar — icons only, per §4.3 of PROJECT-OVERVIEW. Icon names come from
 * shot `2a`.
 *
 * Story 0.4 filters the list to **modules enabled for the selected client
 * (BR-07/BR-11) ∩ modules the user's grupos permit (BR-18)**.
 *
 * 🔒 A disabled module and an unpermitted one look **identical** here, deliberately
 * (`PERSONAS.md`): a Cliente must not be able to infer what iOX sells to others.
 *
 * The empty case renders — §4.3 is explicit that a user with no permitted module is
 * a legitimate state, not a crash.
 */
export function Sidebar({ modulos }: { modulos?: Modulo[] }) {
  const { idCliente } = useParams()
  const { sessao } = useSession()
  const permissoes = usePermissoes()
  const flags = useFlags()

  /**
   * 🔒 O cliente do link, que NÃO é sempre o da URL.
   *
   * Administração não é escopada por cliente (BR-19), então dentro de `/admin/*`
   * não existe `:idCliente` — e a sidebar continua ali, oferecendo os módulos que
   * são. Sem esta resolução ela montava `/cliente//finops`, o router normalizava
   * para `/cliente/finops`, e o usuário recebia "o cliente finops não está na sua
   * lista de acesso".
   *
   * A precedência é a MESMA de `DestinoDeCliente`, e mora num lugar só
   * (`tenant/persistencia`): o cliente lembrado ganha, senão o primeiro da lista —
   * já revalidada contra `{clientes}`. Voltar da Administração cai no cliente em
   * que a pessoa estava, e não num arbitrário.
   */
  const clienteDoLink = idCliente ?? clienteEmFoco(sessao?.clientes ?? [])

  const visiveis =
    modulos ??
    MODULOS.filter(
      (modulo) => acessoDe(permissoes, modulo.permissao).ver && (flags[modulo.flag] ?? true),
    )

  return (
    <nav
      aria-label="Módulos"
      className="border-border-subtle bg-surface-card flex w-18 shrink-0 flex-col items-center gap-2 border-r py-6"
    >
      {visiveis.length === 0 ? (
        <p className="text-text-muted px-2 text-center type-sm" role="status">
          sem módulos
        </p>
      ) : (
        visiveis.map((modulo) => {
          const destino = basePath(modulo, clienteDoLink)
          // Sem cliente algum, um módulo escopado não TEM endereço válido. Some da
          // lista em vez de virar um link quebrado — mesma leitura de
          // `DestinoDeCliente`, que trata "nenhum cliente atribuído" como estado
          // legítimo e não como falha.
          if (!destino) return null
          return (
            <NavLink
              key={modulo.id}
              to={destino}
              title={modulo.rotulo}
              className={({ isActive }) =>
                [
                  'rounded-field flex h-12 w-12 items-center justify-center transition-colors duration-base ease-out',
                  isActive
                    ? 'bg-navy-50 text-iox-navy'
                    : 'text-icon-muted hover:bg-subtle hover:text-icon-default',
                ].join(' ')
              }
            >
              <i className={`ph ph-${modulo.icone} text-2xl`} aria-hidden="true" />
              <span className="sr-only">{modulo.rotulo}</span>
            </NavLink>
          )
        })
      )}

      <BotaoSair />
    </nav>
  )
}

/**
 * Logout — story 0.3 AC-10.
 *
 * ⚠️ **The handoff draws no logout control anywhere**: shot `2c` is cropped to the
 * module frame and no wireframe shows the sidebar's foot. An application with no way
 * out is broken, so it goes at the bottom of the sidebar — the conventional place,
 * and the only surface that persists across every screen. **Flagged for the next
 * alinhamento round; move it if Eduardo places it elsewhere.**
 */
function BotaoSair() {
  const { sessao, sair } = useSession()

  if (!sessao) return null

  return (
    <button
      type="button"
      onClick={() => void sair()}
      title={`Sair — ${sessao.nome}`}
      className="rounded-field text-icon-muted hover:bg-subtle hover:text-icon-default mt-auto flex h-12 w-12 items-center justify-center transition-colors duration-base ease-out"
    >
      <i className="ph ph-sign-out text-2xl" aria-hidden="true" />
      <span className="sr-only">Sair</span>
    </button>
  )
}
